import { Component, Inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Location, LocationDialogData } from '@core/interfaces';
import { ContactItemValidator } from '@validators/contact-item-validation';
import { sortBy } from 'lodash';

@Component({
  selector: 'tab-contacts',
  templateUrl: './tab-contacts.component.html',
  styleUrls: ['./tab-contacts.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TabContactsComponent implements OnInit {
  @Input() contactForm: FormGroup;
  @Input() isViewMode: boolean;
  location: Location;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LocationDialogData,
    private fb: FormBuilder,
  ) {
    this.location = this.data.location;
  }

  ngOnInit(): void {
    this.setContactItem('emails');
    this.setContactItem('phones');

    if (this.isViewMode) {
      this.contactForm.disable();
    }
  }

  private setContactItem(itemsName: string): void {
    const isEmails = Boolean(itemsName === 'emails');

    if (!this.location) {
      return;
    }

    const contactItems = sortBy(this.location.contact[itemsName], 'primary').reverse();

    if (contactItems.length) {
      const someIsPrimary = contactItems.some((contactItem) => contactItem.primary);

      if (!someIsPrimary) {
        contactItems[0].primary = true;
      }
    }

    const contactItemsArray = this.fb.array(contactItems.map((contactItem) => {
      if (isEmails) {
        return this.fb.group({
          emailAddress: this.fb.control(contactItem.emailAddress, [Validators.required, Validators.email]),
          name: this.fb.control(contactItem.name),
          contactType: this.fb.control(contactItem.contactType, Validators.required),
          primary: this.fb.control(contactItem.primary)
        });
      }

      return this.fb.group({
        number: this.fb.control(contactItem.number, Validators.required),
        name: this.fb.control(contactItem.name),
        contactType: this.fb.control(contactItem.contactType, Validators.required),
        primary: this.fb.control(contactItem.primary)
      });
    }));

    if (isEmails) {
      contactItemsArray.setValidators(ContactItemValidator.emailAddressDuplicated);
    } else {
      contactItemsArray.setValidators(ContactItemValidator.phoneNumberDuplicated);
    }

    this.contactForm.setControl(itemsName, contactItemsArray);
  }

  addNewContactItem(itemsName: string): void {
    const itemsNameIsEmail = Boolean(itemsName === 'emails');
    const numberContactItems = this.contactForm.get(itemsName)['controls'].length;
    const contactItemsArray = this.contactForm.get(itemsName) as FormArray;

    const newContactFormGroup = this.fb.group({
      contactType: this.fb.control('work', Validators.required),
      primary: this.fb.control(numberContactItems ? false : true)
    });

    if (itemsNameIsEmail) {
      newContactFormGroup.setControl('emailAddress', this.fb.control(null, [Validators.required, Validators.email]));
      newContactFormGroup.setControl('name', this.fb.control(null));
      contactItemsArray.setValidators(ContactItemValidator.emailAddressDuplicated);
    } else {
      newContactFormGroup.setControl('number', this.fb.control(null, Validators.required));
      newContactFormGroup.setControl('name', this.fb.control(null));
      contactItemsArray.setValidators(ContactItemValidator.phoneNumberDuplicated);
    }

    const lastPosition = contactItemsArray.length;
    contactItemsArray.setControl(lastPosition, newContactFormGroup);

    const inputId = `contact-${itemsName}-${lastPosition}`;
    setTimeout(() => document.getElementById(inputId).focus(), 0);
  }

  removeContactItem(itemsName: string, itemIndex: number): void {
    const contactItemArray = this.contactForm.get(itemsName) as FormArray;
    contactItemArray.removeAt(itemIndex);

    const numberContactItemArray = contactItemArray.controls.length;
    const numberContactItemsPrimary = contactItemArray.value.filter((contactItem) => (
      contactItem.primary === true
    )).length;

    if (numberContactItemArray && !numberContactItemsPrimary) {
      contactItemArray.at(0).get('primary').setValue(true);
    }
  }

  changePrimaryContactItem(itemsName: string, itemIndex: number): void {
    const contactItemArray = this.contactForm.get(itemsName) as FormArray;
    const contactItem = contactItemArray.at(itemIndex);
    const isPrimary = contactItem.get('primary').value;

    const otherControls = contactItemArray.controls.filter((_control, index) => index !== itemIndex);

    if (isPrimary) {
      otherControls.forEach((control) => control.get('primary').setValue(false));
    } else {
      const control = otherControls[0] || contactItemArray.at(itemIndex);
      control.get('primary').setValue(true);
    }
  }
}
